import React, {FC, useCallback, useEffect, useRef, useState} from "react";
import { useTranslation } from "react-i18next";
import { icons } from "../../icons";
import { Button, ClipBoardWrapper, ModalVariables, Table, TableSkeleton, TopProgress, Typography } from "../../components";
import { ServiceActivationContent, ServiceActivationState } from "../../components/service-activation/styles";
import { Container } from '../../components/start-page/styles';
import { isDedicatedServiceInactiveError } from "../../utils/dedicated-service";
import { useNavigate } from "react-router";
import { useQueryParam, NumberParam, StringParam } from 'use-query-params';
import { bareMetalService, } from "../../api";
import { DedicatedServerOperation, DedicatedServerPowerAction } from "../../api/bareMetalTypes";
import { useUi } from "../../context/ui.context";
import { useAuth } from "../../context/user.context";
import { compareArrays } from "../../utils/findIntersection";
import RebootIc from './ic-reboot.svg'
import RestartIc from './ic-restart.svg'
import Preorders from './preorders';
import { dedicatedOperationName } from '../../utils/dedicated-service';
import { hardwareLines, operationProgress, osIcon, serverState, stateColors } from './list/format';
import {
  Count, EmptyState, Header, HeaderActions, HeaderText, Muted, NameWithSpinner, OperationName, OsCell, Page, Panel,
  Primary, Progress, ProgressBar, ProgressText, Spinner, Stack, StatBar, StatCard, StatLabel, StatSub, StatValue,
  StatValueRow, StatsGrid, StateLabel, Subtitle, Tag, Tags,
} from './list/styles';

const BareMetalServers:FC = () => {
  const [servers, setServers] = useState<any[]>([])
  const [operations, setOperations] = useState<DedicatedServerOperation[]>([])
  const [page] = useQueryParam('page', NumberParam);
  const [pageSize] = useQueryParam('page_size', NumberParam);
  const [search] = useQueryParam('search', StringParam);
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [activePreorders, setActivePreorders] = useState<number>()
  const [serviceActivationRequired, setServiceActivationRequired] = useState(false)
  const [serviceOrderLoading, setServiceOrderLoading] = useState(false)
  const serverRequestId = useRef(0)
  const serviceOrderId = useRef(0)
  const serviceOrderPending = useRef(false)
  const [paginationData, setPaginationData] = useState<any>()
  const [viewModal, setViewModal] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingComplete, setLoadingComplete] = useState<boolean>(false)
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false)
  const [modalType, setModalType] = useState<any>('')
  const [alertTitle, setAlertTitle] = useState<any>('')
  const [alertSubTitle, setAlertSubTitle] = useState<any>()
  const [selectedServer, setSelectedServer] = useState<any>()
  const [allChecked, setAllChecked] = useState<boolean>(false)
  const [checked, setChecked] = useState<any[]>([])
  const {
    viewAlert,
    setViewModalComplete,
    setActiveAnimationComplete,
  } = useUi()
  const {user} = useAuth()
  const activeClientId = user?.user?.active_client
  const editChecked = (id:any, isAll?:boolean) => {
    if (id?.isOperation) return
    if(id === null) {
      setChecked([])
      return
    }
    const founded = checked.find((item:any) => item.id === id?.id)
    if(founded) {
      if(isAll){ 
        return 
      } else {
        setChecked((prev:any) => {
          return prev.filter((item:any) => item.id !== id?.id)
        })
        setAllChecked(false)
      }
    } else {
      setChecked((prev) => [...prev, id])
    }
  } 
  useEffect(() => {
    if(compareArrays(servers, checked) && checked?.length !== 0){
      setAllChecked(true)
    }
  },[checked])
  const [t] = useTranslation()
  const navigate = useNavigate()
  const _updateImages = useCallback(async () => {
    const requestId = ++serverRequestId.current
    try {
      const res = await bareMetalService.getListMyServers({page: page ?? undefined, page_size: pageSize ?? undefined})
      if (requestId !== serverRequestId.current) return

      setServers(res.data.objects.map((item:any) => {
        const state = serverState(item, t)
        const hardware = hardwareLines(item.hardware)
        const icon = osIcon(item.os)
        const tags = Array.isArray(item.tags) ? item.tags : []
        return {
          ...item,
          navigate:`${item.id}`,
          clearName:item.hostname || item.name,
          searchText:[item.hostname, item.name, item.primary_ip, ...tags].filter(Boolean).join(' ').toLowerCase(),
          state: (
            <Stack>
              <StateLabel $color={state.color}>{state.label}</StateLabel>
              <Muted>{state.sub}</Muted>
            </Stack>
          ),
          hardware: (
            <Stack>
              <Primary>{hardware.cpu}</Primary>
              {hardware.memDisk ? <Muted>{hardware.memDisk}</Muted> : null}
            </Stack>
          ),
          access_ip: item.primary_ip ?
            <ClipBoardWrapper>{item.primary_ip}</ClipBoardWrapper>
          : <Muted style={{fontSize:15}}>{t('DEDICATED_NO_IP')}</Muted>,
          os: (
            <OsCell>
              {icon ? <img src={icon} alt="" /> : null}
              <span>{item.os || t('DEDICATED_NO_OS')}</span>
            </OsCell>
          ),
          name: (
            <Stack style={{gap:4}}>
              <span>{item.hostname || item.name}</span>
              {tags.length ? <Tags>{tags.map((tag:any) => <Tag key={tag}>{tag}</Tag>)}</Tags> : null}
            </Stack>
          ),
        }
      }))
      setPaginationData(res.data)
      setOperations(res.data.operations || [])
      setServiceActivationRequired(false)
    } catch (error:any) {
      if (requestId !== serverRequestId.current) return

      if (isDedicatedServiceInactiveError(error)) {
        setServers([])
        setOperations([])
        setPaginationData(undefined)
        setChecked([])
        setAllChecked(false)
        setViewModal(false)
        setServiceActivationRequired(true)
      } else {
        serviceOrderPending.current = false
        setServiceOrderLoading(false)
        if (error?.response?.status === 403) {
          navigate('/403', { replace: true })
        } else {
          viewAlert({
            severity:'error',
            message:error?.response?.data?.detail || t("ERROR_OCCURRED"),
          })
        }
      }
    } finally {
      if (requestId === serverRequestId.current) setPageLoading(false)
    }
  }, [navigate, page, pageSize, t, viewAlert])

  useEffect(() => {
    setPageLoading(true)
    setOperations([])
    setServiceActivationRequired(false)
    setServiceOrderLoading(false)
    serviceOrderPending.current = false
    void _updateImages()
    return () => {
      serverRequestId.current += 1
      serviceOrderId.current += 1
    }
  }, [_updateImages, activeClientId])

  useEffect(() => {
    if (serviceActivationRequired && !serviceOrderLoading) return

    const interval = setInterval(() => {
      void _updateImages()
    }, 5000)
    return () => clearInterval(interval)
  }, [_updateImages, serviceActivationRequired, serviceOrderLoading])

  useEffect(() => {
    if (!serviceOrderLoading || serviceActivationRequired) return

    serviceOrderPending.current = false
    setServiceOrderLoading(false)
    viewAlert({ severity: 'success', message: `${t("DEDICATED_SERVICE_ACTIVATED")}` })
  }, [serviceActivationRequired, serviceOrderLoading, t, viewAlert])

  const createServiceOrder = async () => {
    if (serviceOrderPending.current) return

    const orderId = ++serviceOrderId.current
    serviceOrderPending.current = true
    setServiceOrderLoading(true)
    try {
      await bareMetalService.createServiceOrder()
      if (orderId !== serviceOrderId.current) return
      await _updateImages()
    } catch (error:any) {
      if (orderId !== serviceOrderId.current) return
      serviceOrderPending.current = false
      setServiceOrderLoading(false)
      viewAlert({ severity: 'error', message: error?.response?.data?.detail || t("ERROR_OCCURRED") })
    }
  }
  
  const _deleteServer = async () => {
    setLoading(true);
    try {
      if (allChecked) {
        await Promise.all(servers.map((item:any) => bareMetalService.deleteServerById(item.id)));
        _updateImages();
        setAllChecked(false);
        setChecked([]);
      } else if (checked?.length > 0) {
        await Promise.all(checked.map((item) => bareMetalService.deleteServerById(item.id)));
        _updateImages();
        setChecked([]);
      } else if (selectedServer) {
        await bareMetalService.deleteServerById(selectedServer.id);
        _updateImages();
      }
      await setLoadingComplete(true)
      await setTimeout(() => {
        setLoading(false);
        setLoadingComplete(false);
        setViewModal(false);
        setActiveAnimation(false);
        setModalType('')
      },1500)
    } catch (e:any) {
      if (e?.response?.data.detail) {
        viewAlert({
          severity: 'error',
          message: e.response.data.detail,
        });
      }
      setChecked([]);
      setLoading(false);
    } 
  };
  const _StartStopServer = async (status:DedicatedServerPowerAction) => {
    setLoading(true);
    try {
      if (allChecked) {
        await Promise.all(servers.map((item:any) => bareMetalService.changePowerStatus(item.id, status)));
        _updateImages();
        setAllChecked(false);
        setChecked([]);
      } else if (checked?.length > 0) {
        await Promise.all(checked.map((item) => bareMetalService.changePowerStatus(item.id, status)));
        _updateImages();
        setChecked([]);
      } else if (selectedServer) {
        await bareMetalService.changePowerStatus(selectedServer.id, status)
        _updateImages();
      }
      await setLoadingComplete(true)
      await setTimeout(() => {
        setLoading(false);
        setLoadingComplete(false);
        setViewModal(false);
        setActiveAnimation(false);
        setModalType('')
      },1500)
    } catch (e:any) {
      if (e?.response?.data.detail) {
        viewAlert({
          severity: 'error',
          message: e.response.data.detail,
        });
      }
      setChecked([]);
      setLoading(false);
    } 
  };
  const openModal = (modal:any) => {
    setModalType(modal)
    setViewModal(true)
    setActiveAnimation(true)
  }
  const closeModal = async (values:any) => {
    let res:any;
    try {
      setLoading(true);
      switch (modalType) {
        case 'baremetal-add-floating':
          res = await bareMetalService.addFloating(selectedServer.id, values)
          if (res?.data?.detail) {
            viewAlert({
              severity: 'info',
              message: res.data?.detail,
            });
          }
          break;
        case 'baremetal-dissociate-floating':
          res = await bareMetalService.removeFloating(selectedServer.id, values)
          if (res?.data?.detail) {
            viewAlert({
              severity: 'info',
              message: res.data?.detail,
            });
          }
          break;
        case 'delete':
          _deleteServer()
          break;

        case 'reinstall-baremetal':
          res = await bareMetalService.reinstallServer(selectedServer.id, values)
          if (res?.data?.detail) {
            viewAlert({
              severity: 'info',
              message: res.data?.detail,
            });
          }
          break;
        case 'edit-baremetal':
          res = await bareMetalService.editServer(selectedServer.id, values)
          if (res?.data?.detail) {
            viewAlert({
              severity: 'info',
              message: res.data?.detail,
            });
          }
          break;
        case 'universal':
          // setLoading(true);
          if (alertTitle === `${t('STOP_SERVER')}` || alertTitle === `${t('STOP_SERVERS')}`) {
            _StartStopServer('stop')
          } else if (alertTitle === `${t('START_SERVER')}` || alertTitle === `${t('START_SERVERS')}`) {
            _StartStopServer('start')
          } else if (alertTitle === `${t('RESTART_SERVER')}`) {
            _StartStopServer('restart')
          } else if (alertTitle === `${t('REBOOT_SERVER')}`) {
            _StartStopServer('reboot')
            // res = await bareMetalService.changePowerStatus(bareMetal.id, 'reboot');
          }
  
          if (res?.data?.detail) {
            viewAlert({
              severity: 'info',
              message: res.data?.detail,
            });
          }
          break;
        }
        if(modalType !== 'delete' && modalType !== 'universal'){
          await setLoadingComplete(true)
          await setTimeout(() => {
            setLoading(false);
            setLoadingComplete(false);
            setViewModal(false);
            setActiveAnimation(false);
            setModalType('')
          },1500)
        }
        // await setViewModal(false);
        // await setActiveAnimation(false);
        // await setModalType('')
      } catch (e: any) {
        console.log('e',e);
        if (e?.response?.data.detail) {
          viewAlert({
            severity: 'error',
            message: e.response.data.detail,
          });
        }
      } finally {
        if(modalType !== 'delete' && modalType !== 'universal'){
          setLoading(false);
        }
      }
    // setViewModal(false)
    // setActiveAnimation(false)
  }
  const closeModalCancel = () => {
    setViewModal(false)
    setActiveAnimation(false)
  }
  const confirmPower = (server:any, title:string, question:string) => {
    setSelectedServer(server)
    setAlertTitle(title)
    setAlertSubTitle(`${question} ${server?.clearName}?`)
    openModal('universal')
  }
  const items = [
    {
      title:t("DEDICATED_POWER_ON"),
      hint:t("DEDICATED_HINT_START"),
      group:t("DEDICATED_MENU_POWER"),
      id:'start',
      icon:icons.PlayIcon,
      onClick:(server:any) => confirmPower(server, `${t("START_SERVER")}`, `${t("ARE_YOU_SHURE_WANT_TO_START_SERVERS")}`),
      divider:false,
    },
    {
      title:t("DEDICATED_SHUT_DOWN"),
      group:t("DEDICATED_MENU_POWER"),
      id:'stop',
      icon:icons.Power,
      onClick:(server:any) => confirmPower(server, `${t("STOP_SERVER")}`, `${t("ARE_YOU_SHURE_WANT_TO_STOP_SERVERS")}`),
      divider:false,
    },
    {
      title:t("REBOOT"),
      id:'reboot',
      icon:RebootIc,
      onClick:(server:any) => confirmPower(server, `${t("REBOOT_SERVER")}`, `${t("ARE_YOU_SHURE_WANT_TO_REBOOT_SERVER")}`),
      divider:false,
    },
    {
      title:t("RESTART"),
      id:'restart',
      icon:RestartIc,
      onClick:(server:any) => confirmPower(server, `${t("RESTART_SERVER")}`, `${t("ARE_YOU_SHURE_WANT_TO_RESTART_SERVER")}`),
      divider:true,
    },
    {
      title:t("REINSTALL"),
      hint:t("DEDICATED_HINT_REINSTALL"),
      group:t("DEDICATED_MENU_MANAGE"),
      id:'reinstall',
      icon:icons.Rebuild,
      onClick: (server:any) => {
        setSelectedServer(server)
        openModal('reinstall-baremetal')
      },
      divider:false
    },
    {
      title:t("DEDICATED_EDIT_NAME_TAGS"),
      id:'edit',
      icon:icons.Edit,
      onClick: async (server:any) => {
        try {
          const {data} = await bareMetalService.getServerById(server.id)
          setSelectedServer(data)
          openModal('edit-baremetal')
        } catch (err:any) {
          viewAlert({severity:'error', message:err?.response?.data?.detail || t('ERROR_OCCURRED')})
        }
      },
      divider:false
    },
    {
      title:t("ASSIGN_FLOATING_IP"),
      onClick:(server:any) => {
        setSelectedServer(server)
        setAlertTitle(server.clearName)
        openModal('baremetal-add-floating')
      },
      id:'fip-add',
      icon:icons.PlusDefault,
      divider:false
    },
    {
      title:t("DISSOCIATE_IP"),
      onClick:(server:any) => {
        setSelectedServer(server)
        setAlertTitle(server.clearName)
        openModal('baremetal-dissociate-floating')
      },
      id:'fip-remove',
      icon:icons.MinusDefault,
      divider:true
    },
    {
      title:t("DEDICATED_DELETE_SERVER"),
      danger:true,
      id:'delete',
      onClick:(server:any) => {
        openModal('delete')
        setAlertTitle(t("BARE_METAL"))
        setAlertSubTitle([server])
        setSelectedServer(server)
      },
      icon:icons.DeleteIcon,
      divider:false
    },
  ]
  // A stopped server can only be started; a running one can be stopped or rebooted.
  const filterSettings = (row:any, options:any[]) => {
    const offline = row?.power_status === 'offline'
    return options.filter((item) => offline ? !['stop', 'reboot', 'restart'].includes(item.id) : item.id !== 'start')
  }
  const Columns = [
    { title:t("NAME"), key:'name' },
    { title:t("DEDICATED_STATE"), key:'state' },
    { title:t("DEDICATED_HARDWARE"), key:'hardware', cardFullWidth:true },
    { title:t("DEDICATED_PUBLIC_IP"), key:'access_ip' },
    { title:t("OS"), key:'os' },
    { title:'', key:'props' },
  ]
  const _createFunc = (mode?:'preorder') => {
    if(!user.user.active_client){
      navigate('/complete-registration')
    } else if(user.requires_email_verification){
      setViewModalComplete(true)
      setActiveAnimationComplete(true)
    } else {
      navigate(mode ? 'create?mode=preorder' : 'create')
    }
  }
  if (serviceActivationRequired) return (
    <Container>
      <TopProgress loading={serviceOrderLoading}/>
      <ServiceActivationState>
        <ServiceActivationContent>
          <Typography variant="h2">{t("DEDICATED_SERVICE_REQUIRED")}</Typography>
          <Typography variant="body">{t("DEDICATED_SERVICE_REQUIRED_DESCRIPTION")}</Typography>
          <Button
            variant="primary"
            size="display"
            loading={serviceOrderLoading}
            onClick={createServiceOrder}
            title={t("ACTIVATE_DEDICATED_SERVICE")}
          />
        </ServiceActivationContent>
      </ServiceActivationState>
    </Container>
  )

  const operationRows = operations.map((operation) => {
    const progress = operationProgress(operation)
    return {
      ...operation,
      name: (
        <NameWithSpinner>
          <Spinner aria-hidden="true" />
          <Stack>
            <OperationName>{dedicatedOperationName(operation)}</OperationName>
            <Muted>{t('DEDICATED_OPERATION_NEW_SERVER')}</Muted>
          </Stack>
        </NameWithSpinner>
      ),
      navigate: `/operations/${operation.id}`,
      isOperation: true,
      hideCheckbox: true,
      hideProps: true,
      state: (
        <Progress role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress.pct ?? undefined}
          aria-label={progress.step || String(t('DEDICATED_OPERATION_IN_PROGRESS'))}>
          <ProgressText>
            <span>{progress.step || t('DEDICATED_OPERATION_IN_PROGRESS')}</span>
            {progress.pct !== null ? <span>{progress.pct}%</span> : null}
          </ProgressText>
          <ProgressBar $pct={progress.pct} />
        </Progress>
      ),
    }
  })

  const needle = (search || '').trim().toLowerCase()
  const visibleServers = needle ? servers.filter((server) => server.searchText.includes(needle)) : servers
  const visibleOperations = needle ? operationRows.filter((operation) => dedicatedOperationName(operation).toLowerCase().includes(needle)) : operationRows
  const serverCount = paginationData?.count ?? paginationData?.totalCount ?? servers.length
  // Same rules as the State column, so every server is counted once.
  const isBusy = (server:any) => server.reinstall_running || ['creating', 'installing', 'deleting'].includes(server.status)
  const onlineCount = servers.filter((server) => !isBusy(server) && server.status === 'active' && server.power_status === 'online').length
  const busyCount = operations.length + servers.filter(isBusy).length
  const fleetSize = servers.length + operations.length
  const percent = (value:number, total:number) => total > 0 ? Math.round(value / total * 100) : 0
  const selection = checked.length ? checked : []
  const bulk = (title:string, question:string) => {
    setAlertTitle(title)
    setAlertSubTitle(`${question} ${selection.map((ch:any) => ch.clearName)}?`)
    openModal('universal')
  }

  return (
    <Container style={{ gap: 20 }}>
      <TopProgress loading={pageLoading} />
      <Page>
        <Header>
          <HeaderText>
            <Typography variant="h1">{t('BARE_METAL')}</Typography>
            <Subtitle>{t('DEDICATED_LIST_SUBTITLE')}</Subtitle>
          </HeaderText>
          <HeaderActions>
            <Button variant="stroke" size="display" title={t('DEDICATED_NEW_PREORDER')} onClick={() => _createFunc('preorder')} />
            <Button variant="primary" size="display" title={t('CREATE_BARE_METAL')} onClick={() => _createFunc()} />
          </HeaderActions>
        </Header>

        <StatsGrid aria-label={t('DEDICATED_SUMMARY')}>
          <StatCard>
            <StatLabel>{t('SERVERS')}</StatLabel>
            <StatValueRow>
              <StatValue>{serverCount}</StatValue>
              <StatSub>
                {t('DEDICATED_STAT_ONLINE', { count: onlineCount })}
                {busyCount ? ` · ${t('DEDICATED_STAT_BUSY', { count: busyCount })}` : ''}
              </StatSub>
            </StatValueRow>
            <StatBar aria-hidden="true" $segments={[
              { pct: percent(onlineCount, fleetSize), color: stateColors.ok },
              { pct: percent(busyCount, fleetSize), color: stateColors.warn },
            ]} />
          </StatCard>
          <StatCard>
            <StatLabel>{t('DEDICATED_PREORDERS')}</StatLabel>
            <StatValueRow><StatValue>{activePreorders ?? '—'}</StatValue><StatSub>{t('DEDICATED_STAT_PREORDERS_SUB')}</StatSub></StatValueRow>
            <StatBar aria-hidden="true" $segments={[{ pct: activePreorders ? 100 : 0, color: stateColors.blue }]} />
          </StatCard>
        </StatsGrid>

        <Panel aria-labelledby="dedicated-servers-title">
          {pageLoading ? <TableSkeleton rows={3} columns={Columns.length} columnsData={Columns} />
            : servers.length || operations.length ? <Table
              title={<h2 id="dedicated-servers-title" style={{margin:0, font:'inherit'}}>{t('SERVERS')} <Count>· {serverCount}</Count></h2>}
              searchable
              searchPlaceholder={t('DEDICATED_SEARCH_SERVERS')}
              hasActiveFilters={Boolean(needle)}
              columns={Columns} rows={[...visibleOperations, ...visibleServers]} settings={items} filterSettings={filterSettings}
              allChecked={allChecked} setAllChecked={setAllChecked} checked={checked} setChecked={editChecked}
              paginationData={paginationData} updateData={_updateImages} allowCardView
              selectionActions={<>
                <Button variant="stroke" size="mid" title={t("DEDICATED_POWER_ON")} onClick={() => bulk(`${t("START_SERVERS")}`, `${t("ARE_YOU_SHURE_WANT_TO_START_SERVERS")}`)} />
                <Button variant="stroke" size="mid" title={t("DEDICATED_SHUT_DOWN")} onClick={() => bulk(`${t("STOP_SERVERS")}`, `${t("ARE_YOU_SHURE_WANT_TO_STOP_SERVERS")}`)} />
                <Button variant="stroke" size="mid" isDelete title={t('DELETE')} onClick={() => {
                  setAlertTitle(t("BARE_METAL_SERVERS"))
                  setAlertSubTitle(allChecked ? servers : checked)
                  openModal('delete')
                }} />
              </>}
            /> : <>
              <h2 id="dedicated-servers-title" style={{margin:0, fontSize:18, lineHeight:'24px', fontWeight:500}}>{t('SERVERS')}</h2>
              <EmptyState>
                <span>{t('THERE_ARE_NO_BARE_METAL_YET')}</span>
                <Button variant="primary" size="display" title={t('CREATE_BARE_METAL')} onClick={() => _createFunc()} />
              </EmptyState>
            </>}
        </Panel>
        {paginationData && <Preorders onActiveCount={setActivePreorders} />}
      </Page>
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        alertSubTitle={alertSubTitle}
        loading={loading}
        loadingComplete={loadingComplete}
        instance={selectedServer}
      />
    </Container>
  )
}


const BareMetal: FC = () => {
  const { user } = useAuth()
  return <BareMetalServers key={user?.user?.active_client} />
}

export default BareMetal
